<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import type { SortOption } from '../types';
import FilterContent from './FilterContent.vue';
import Filter from './icons/Filter.vue';
import X from './icons/X.vue';

interface Props {
  isOpen: boolean;
  isMobile: boolean;
  allTags: string[];
  selectedTags: string[];
  sortBy: SortOption;
  minScore: number;
  searchQuery: string;
  groupedDots: boolean;
  visibleVoteTypes: ('liked' | 'important' | 'supported')[];
  groupByTag?: boolean;
  displayMode?: 'grid' | 'list';
}

const props = defineProps<Props>();

const emit = defineEmits({
  close: () => true,
  'update:selectedTags': (_tags: string[]) => true,
  'update:sortBy': (_sort: SortOption) => true,
  'update:minScore': (_score: number) => true,
  'update:searchQuery': (_query: string) => true,
  'update:groupedDots': (_grouped: boolean) => true,
  'update:groupByTag': (_grouped: boolean) => true,
  'update:displayMode': (_mode: 'grid' | 'list') => true,
  toggleVoteType: (_type: 'liked' | 'important' | 'supported') => true,
  clearFilters: () => true,
});

const activeFilterCount = computed(() => {
  let count = 0;
  if (props.selectedTags.length > 0) count += props.selectedTags.length;
  if (props.minScore > 0) count += 1;
  if (props.searchQuery) count += 1;
  return count;
});

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.isOpen) {
    emit('close');
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<script lang="ts">
import { computed } from 'vue';
</script>

<template>
  <Teleport to="body">
    <!-- Mobile: Full screen overlay sliding up from bottom -->
    <Transition name="fade">
      <div
        v-if="isOpen && isMobile"
        class="fixed inset-0 bg-black/50 z-40"
        @click="$emit('close')"
      />
    </Transition>

    <Transition name="slide-up">
      <div
        v-if="isOpen && isMobile"
        class="fixed inset-x-0 bottom-0 top-16 bg-white z-50 flex flex-col rounded-t-2xl overflow-hidden"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200">
          <div class="flex items-center gap-2">
            <Filter class="w-5 h-5 text-gray-700" />
            <h2 class="font-bold text-lg text-gray-900">Filters</h2>
            <span
              v-if="activeFilterCount > 0"
              class="text-xs bg-(--color-primary) text-white px-2 py-0.5 rounded-full"
            >
              {{ activeFilterCount }}
            </span>
          </div>
          <button
            @click="$emit('close')"
            class="cursor-pointer p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close filters"
          >
            <X class="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-4 scrollbar-white">
          <FilterContent
            :all-tags="allTags"
            :selected-tags="selectedTags"
            :sort-by="sortBy"
            :min-score="minScore"
            :search-query="searchQuery"
            :grouped-dots="groupedDots"
            :visible-vote-types="visibleVoteTypes"
            :group-by-tag="groupByTag"
            :display-mode="displayMode"
            @update:selected-tags="$emit('update:selectedTags', $event)"
            @update:sort-by="$emit('update:sortBy', $event)"
            @update:min-score="$emit('update:minScore', $event)"
            @update:search-query="$emit('update:searchQuery', $event)"
            @update:grouped-dots="$emit('update:groupedDots', $event)"
            @update:group-by-tag="$emit('update:groupByTag', $event)"
            @update:display-mode="$emit('update:displayMode', $event)"
            @toggle-vote-type="$emit('toggleVoteType', $event)"
            @clear-filters="$emit('clearFilters')"
          />
        </div>

        <!-- Footer -->
        <div class="p-4 border-t border-gray-200 space-y-2">
          <button
            v-if="activeFilterCount > 0"
            @click="$emit('clearFilters')"
            class="cursor-pointer w-full py-3 text-(--color-destructive) bg-red-50 rounded-lg font-medium"
          >
            Clear All Filters
          </button>
          <button
            @click="$emit('close')"
            class="cursor-pointer w-full py-3 bg-(--color-primary) text-white rounded-lg font-medium"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </Transition>

    <!-- Desktop: Panel at bottom using page space -->
    <Transition name="slide-up-desktop">
      <div
        v-if="isOpen && !isMobile"
        class="filter-drawer-desktop fixed bottom-0 left-0 right-0 bg-white z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.15)] flex flex-col rounded-t-2xl overflow-hidden max-h-[70vh]"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <div class="flex items-center gap-2">
            <Filter class="w-5 h-5 text-gray-700" />
            <h2 class="font-bold text-gray-900">Filters</h2>
            <span
              v-if="activeFilterCount > 0"
              class="text-xs bg-(--color-primary) text-white px-2 py-0.5 rounded-full"
            >
              {{ activeFilterCount }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="activeFilterCount > 0"
              @click="$emit('clearFilters')"
              class="cursor-pointer text-xs text-(--color-destructive) hover:underline"
            >
              Clear
            </button>
            <button
              @click="$emit('close')"
              class="cursor-pointer p-1 hover:bg-gray-100 rounded transition-colors"
              aria-label="Close filters"
            >
              <X class="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        <!-- Content - horizontal layout for desktop -->
        <div class="flex-1 overflow-y-auto p-4 scrollbar-white">
          <div class="max-w-7xl mx-auto">
            <FilterContent
              :all-tags="allTags"
              :selected-tags="selectedTags"
              :sort-by="sortBy"
              :min-score="minScore"
              :search-query="searchQuery"
              :grouped-dots="groupedDots"
              :visible-vote-types="visibleVoteTypes"
              :group-by-tag="groupByTag"
              :display-mode="displayMode"
              compact
              @update:selected-tags="$emit('update:selectedTags', $event)"
              @update:sort-by="$emit('update:sortBy', $event)"
              @update:min-score="$emit('update:minScore', $event)"
              @update:search-query="$emit('update:searchQuery', $event)"
              @update:grouped-dots="$emit('update:groupedDots', $event)"
              @update:group-by-tag="$emit('update:groupByTag', $event)"
              @update:display-mode="$emit('update:displayMode', $event)"
              @toggle-vote-type="$emit('toggleVoteType', $event)"
              @clear-filters="$emit('clearFilters')"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
